import { useState, useCallback, useEffect } from 'react';
import { Step, CallBackProps } from 'react-joyride';

interface UseGuideReturn {
  run: boolean;
  steps: Step[];
  handleJoyrideCallback: (data: CallBackProps) => void;
  startGuide: () => void;
  resetGuide: () => void;
  skipGuide: () => void;
}

export const useGuide = (guideKey: string, guideSteps: Step[]): UseGuideReturn => {
  const [run, setRun] = useState(false);
  // 直接使用传入的 guideSteps，而不是用 useState 固定
  const steps = guideSteps;

  // 检查用户是否已经完成过引导
  useEffect(() => {
    const hasCompletedGuide = localStorage.getItem(`guide_completed_${guideKey}`);
    
    // 只有在有步骤且未完成过引导时才启动
    if (!hasCompletedGuide && guideSteps.length > 0) {
      console.log(`🚀 [引导启动] 准备启动引导，步骤数: ${guideSteps.length}`);
      // 延迟启动引导，让页面完全加载
      const timer = setTimeout(() => {
        console.log('✅ [引导启动] 引导已启动');
        setRun(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      console.log('⏸️ [引导启动] 不启动引导:', {
        hasCompleted: hasCompletedGuide,
        stepsCount: guideSteps.length
      });
    }
  }, [guideKey, guideSteps.length]);

  // 开始引导
  const startGuide = useCallback(() => {
    if (guideSteps.length > 0) {
      setRun(true);
    }
  }, [guideSteps.length]);

  // 重置引导（用于测试或重新观看）
  const resetGuide = useCallback(() => {
    localStorage.removeItem(`guide_completed_${guideKey}`);
    if (guideSteps.length > 0) {
      setRun(true);
    }
  }, [guideKey, guideSteps.length]);

  // 跳过引导
  const skipGuide = useCallback(() => {
    localStorage.setItem(`guide_completed_${guideKey}`, 'true');
    setRun(false);
  }, [guideKey]);

  // 处理引导回调
  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses = ['finished', 'skipped'];

    if (finishedStatuses.includes(status as string)) {
      setRun(false);
      // 标记引导已完成
      localStorage.setItem(`guide_completed_${guideKey}`, 'true');
    }
  }, [guideKey]);

  return {
    run,
    steps,
    handleJoyrideCallback,
    startGuide,
    resetGuide,
    skipGuide,
  };
};

